import { ImportEstablishmentFormField, type ViewEstablishments } from '@enmon/commons/lib/enmon';
import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import { captureException } from '@sentry/react';
import type {
  CreateEstablishmentArg,
  CreateEstablishmentResult,
  DeleteEstablishmentArg,
  DeleteEstablishmentResult,
  GetCostEfficiencyArg,
  GetCostEfficiencyResult,
  GetEstablishmentArg,
  GetEstablishmentResult,
  GetEstablishmentsArg,
  GetEstablishmentsResult,
  ImportEstablishmentsArg,
  ImportEstablishmentsResult,
  UpdateEstablishmentArg,
  UpdateEstablishmentResult,
} from './types';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export class EstablishmentsApiClient extends BaseApiClient {
  constructor() {
    super('api/establishments');
  }

  public get<TView extends ViewEstablishments = ViewEstablishments.Default>(
    filter: GetEstablishmentsArg,
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<GetEstablishmentsResult<TView>> {
    return this.http.get('', {
      query: getQuery({ ...filter, view, timezone }),
      signal,
    });
  }

  public getOne(
    establishmentId: GetEstablishmentArg,
    signal: RequestOptions['signal'],
  ): Promise<GetEstablishmentResult> {
    return this.http.get(`${establishmentId}`, { signal });
  }

  public create(payload: CreateEstablishmentArg): Promise<CreateEstablishmentResult> {
    return this.http.post('', payload);
  }

  public delete(id: DeleteEstablishmentArg): Promise<DeleteEstablishmentResult> {
    return this.http.delete(`${id}`);
  }

  public update({ establishmentId, payload }: UpdateEstablishmentArg): Promise<UpdateEstablishmentResult> {
    return this.http.patch(`${establishmentId}`, payload);
  }

  public async importEstablishments({ importFile }: ImportEstablishmentsArg): Promise<ImportEstablishmentsResult> {
    const formData = new FormData();
    formData.append(ImportEstablishmentFormField.ImportFile, importFile);

    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, {
        signal: undefined,
      });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<ImportEstablishmentsResult>();
      }
      console.error(e);
      captureException(e);
      throw e;
    }
  }

  public async getCostEfficiency(
    { establishmentId, filter }: GetCostEfficiencyArg,
    signal: RequestOptions['signal'],
  ): Promise<GetCostEfficiencyResult> {
    return await this.http.get(`${establishmentId}/cost-efficiency`, {
      query: getQuery(filter),
      signal,
    });
  }
}
